/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */

import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Save,
  Plus,
  Check,
  AlertCircle,
  Settings,
  Zap,
  Bold,
  Italic,
  List,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Underline,
} from "lucide-react";

// Options
const TRIGGERS = [
  "Caregiver Work Starts",
  "Job Application Accepted",
  "Manual Send",
  "Subscription Renewal",
  "Onboarding Completed",
];

const SERVICE_TYPES = [
  "Childcare",
  "Cook",
  "Cleaner",
  "Home Tutor",
  "Elderly Care",
  "All Services",
];

const RECIPIENTS_OPTIONS = ["Client", "Caregiver", "Admin", "Agency Partner"];

const DYNAMIC_FIELDS = [
  { label: "Service Provider Name", value: "{{provider_name}}" },
  { label: "Service Buyer Name", value: "{{buyer_name}}" },
  { label: "Provider Phone", value: "{{provider_phone}}" },
  { label: "Buyer Phone", value: "{{buyer_phone}}" },
  { label: "Service Type", value: "{{service_type}}" },
  { label: "Work Period", value: "{{work_period}}" },
  { label: "Payment Amount", value: "{{payment_amount}}" },
  { label: "Payment Terms", value: "{{payment_terms}}" },
  { label: "Agreement Date", value: "{{date}}" },
];

const AgreementBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const textareaRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    isAutomatic: false,
    trigger: "",
    serviceType: "",
    recipients: [],
    content: "",
  });

  // Mock data loading for Edit mode
  useEffect(() => {
    if (isEditMode) {
      // Simulate fetch
      setFormData({
        name: "Standard Caregiver Contract",
        description:
          "This agreement outlines the terms and conditions for caregivers providing standard home care services.",
        isAutomatic: true,
        trigger: "Caregiver Assignment Accepted",
        serviceType: "Childcare",
        recipients: ["Client", "Caregiver"],
        content: `AGREEMENT FOR CAREGIVER SERVICES

This Agreement is made on {{date}} between {{provider_name}} (Service Provider) and {{buyer_name}} (Service Buyer).

1. SERVICES PROVIDED
The Caregiver agrees to provide {{service_type}} services as agreed upon...

2. COMPENSATION
Payment of {{payment_amount}} shall be made according to {{payment_terms}}.

3. CONFIDENTIALITY
The Caregiver agrees to keep all client information confidential...`,
      });
    }
  }, [isEditMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleRecipient = (recipient) => {
    setFormData((prev) => {
      const exists = prev.recipients.includes(recipient);
      return {
        ...prev,
        recipients: exists
          ? prev.recipients.filter((r) => r !== recipient)
          : [...prev.recipients, recipient],
      };
    });
  };

  const insertPlaceholder = (placeholder) => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.content;
      const newText =
        text.substring(0, start) + placeholder + text.substring(end);

      setFormData((prev) => ({ ...prev, content: newText }));

      // Restore cursor position after state update
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd =
          start + placeholder.length;
      }, 0);
    } else {
      // Fallback if ref isn't ready
      setFormData((prev) => ({ ...prev, content: prev.content + placeholder }));
    }
  };

  const handleSave = () => {
    navigate("/agreements");
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12 bg-background dark:bg-dark-background min-h-screen p-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8">
        <div className="flex items-center gap-4 w-full md:w-auto">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-xl hover:bg-accent dark:hover:bg-gray-800 transition-colors text-gray-500 hover:text-text dark:text-dark-muted dark:hover:text-dark-text"
          >
            <ChevronLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-text dark:text-dark-text">
              {isEditMode
                ? "Edit Agreement Template"
                : "Create Agreement Template"}
            </h1>
            <p className="text-gray-500 dark:text-dark-muted mt-1">
              Define settings, triggers, and content for this agreement.
            </p>
          </div>
        </div>
        <div className="flex gap-3 w-full md:w-auto">
          <button className="flex-1 md:flex-none px-6 py-2.5 border border-gray-300 dark:border-dark-border text-gray-700 dark:text-dark-muted rounded-xl font-bold hover:bg-accent dark:hover:bg-gray-800 transition-colors">
            Save as Draft
          </button>
          <button
            onClick={handleSave}
            className="flex-1 md:flex-none px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold shadow-lg shadow-primary/20 dark:shadow-none transition-all flex items-center justify-center gap-2"
          >
            <Save size={18} /> Save Template
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* LEFT COLUMN: Configuration */}
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-card dark:bg-dark-surface rounded-3xl p-6 border border-gray-100 dark:border-dark-border shadow-sm">
            <h3 className="font-bold text-text dark:text-dark-text flex items-center gap-2 mb-6">
              <Settings
                size={20}
                className="text-gray-400 dark:text-dark-muted"
              />{" "}
              Configuration
            </h3>

            <div className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-1.5">
                  Template Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g. Service Contract"
                  className="w-full px-4 py-2.5 bg-accent dark:bg-gray-900 border border-gray-200 dark:border-dark-border rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none text-text dark:text-dark-text"
                />
              </div>

              <div>
                <div className="flex justify-between">
                  <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-1.5">
                    Description
                  </label>
                  <span className="text-[10px] text-gray-400 dark:text-dark-muted">
                    {formData.description.length}/200
                  </span>
                </div>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  maxLength={200}
                  rows={3}
                  placeholder="Brief summary..."
                  className="w-full px-4 py-2.5 bg-accent dark:bg-gray-900 border border-gray-200 dark:border-dark-border rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none resize-none text-text dark:text-dark-text"
                />
              </div>

              <div className="p-4 bg-accent dark:bg-gray-900 rounded-xl flex items-center justify-between">
                <span className="text-sm font-medium text-text dark:text-dark-text">
                  Send automatically on trigger
                </span>
                <button
                  onClick={() =>
                    setFormData((p) => ({ ...p, isAutomatic: !p.isAutomatic }))
                  }
                  className={`relative w-11 h-6 rounded-full transition-colors ${formData.isAutomatic
                    ? "bg-primary"
                    : "bg-gray-300 dark:bg-gray-600"
                    }`}
                >
                  <span
                    className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${formData.isAutomatic ? "translate-x-5" : ""
                      }`}
                  />
                </button>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-1.5">
                  Trigger Event
                </label>
                <div className="relative">
                  <select
                    name="trigger"
                    value={formData.trigger}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-accent dark:bg-gray-900 border border-gray-200 dark:border-dark-border rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none appearance-none text-text dark:text-dark-text"
                  >
                    <option value="">Select Trigger</option>
                    {TRIGGERS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <ChevronLeft
                    className="absolute right-4 top-1/2 -translate-y-1/2 -rotate-90 text-gray-400 dark:text-dark-muted pointer-events-none"
                    size={14}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-1.5">
                  Service Type
                </label>
                <div className="relative">
                  <select
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-accent dark:bg-gray-900 border border-gray-200 dark:border-dark-border rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none appearance-none text-text dark:text-dark-text"
                  >
                    <option value="">Select Service</option>
                    {SERVICE_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <ChevronLeft
                    className="absolute right-4 top-1/2 -translate-y-1/2 -rotate-90 text-gray-400 dark:text-dark-muted pointer-events-none"
                    size={14}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-dark-muted uppercase mb-2">
                  Recipients
                </label>
                <div className="space-y-2">
                  {RECIPIENTS_OPTIONS.map((r) => (
                    <label
                      key={r}
                      className="flex items-center gap-3 cursor-pointer group p-2 hover:bg-accent dark:hover:bg-gray-900 rounded-lg transition-colors"
                    >
                      <div
                        className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${formData.recipients.includes(r)
                          ? "bg-primary border-primary"
                          : "border-gray-300 dark:border-dark-border bg-white dark:bg-gray-800"
                          }`}
                      >
                        {formData.recipients.includes(r) && (
                          <Check size={12} className="text-white" />
                        )}
                      </div>
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={formData.recipients.includes(r)}
                        onChange={() => toggleRecipient(r)}
                      />
                      <span className="text-sm text-text dark:text-dark-text">
                        {r}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CENTER COLUMN: Editor */}
        <div className="lg:col-span-6 flex flex-col min-h-[600px]">
          <div className="bg-card dark:bg-dark-surface rounded-3xl border border-gray-100 dark:border-dark-border shadow-sm flex flex-col flex-1 overflow-hidden">
            {/* Mock Toolbar */}
            <div className="p-3 border-b border-gray-100 dark:border-dark-border bg-accent/50 dark:bg-gray-900/50 flex gap-2">
              <button className="p-2 hover:bg-accent dark:hover:bg-gray-700 rounded text-gray-500 dark:text-dark-muted">
                <Bold size={16} />
              </button>
              <button className="p-2 hover:bg-accent dark:hover:bg-gray-700 rounded text-gray-500 dark:text-dark-muted">
                <Italic size={16} />
              </button>
              <button className="p-2 hover:bg-accent dark:hover:bg-gray-700 rounded text-gray-500 dark:text-dark-muted">
                <Underline size={16} />
              </button>
              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1 self-center"></div>
              <button className="p-2 hover:bg-accent dark:hover:bg-gray-700 rounded text-gray-500 dark:text-dark-muted">
                <AlignLeft size={16} />
              </button>
              <button className="p-2 hover:bg-accent dark:hover:bg-gray-700 rounded text-gray-500 dark:text-dark-muted">
                <AlignCenter size={16} />
              </button>
              <button className="p-2 hover:bg-accent dark:hover:bg-gray-700 rounded text-gray-500 dark:text-dark-muted">
                <AlignRight size={16} />
              </button>
              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1 self-center"></div>
              <button className="p-2 hover:bg-accent dark:hover:bg-gray-700 rounded text-gray-500 dark:text-dark-muted">
                <List size={16} />
              </button>
            </div>

            <div className="flex-1 flex flex-col bg-card dark:bg-dark-surface relative">
              <textarea
                ref={textareaRef}
                value={formData.content}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, content: e.target.value }))
                }
                className="flex-1 w-full h-full p-6 bg-transparent border-none outline-none resize-none font-mono text-sm leading-relaxed text-text dark:text-dark-text"
                placeholder="Draft your agreement content here..."
                spellCheck={false}
              />
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Components */}
        <div className="lg:col-span-3">
          <div className="bg-card dark:bg-dark-surface rounded-3xl p-6 border border-gray-100 dark:border-dark-border shadow-sm sticky top-6">
            <h3 className="font-bold text-text dark:text-dark-text flex items-center gap-2 mb-6">
              <Zap size={20} className="text-yellow-500" /> Add Components
            </h3>
            <p className="text-xs text-gray-500 dark:text-dark-muted mb-4">
              Click to insert dynamic fields into your agreement body.
            </p>

            <div className="space-y-2">
              {DYNAMIC_FIELDS.map((field) => (
                <button
                  key={field.value}
                  onClick={() => insertPlaceholder(field.value)}
                  className="w-full flex items-center justify-between p-3 rounded-xl bg-accent dark:bg-gray-900 border border-gray-200 dark:border-dark-border hover:border-primary hover:bg-primary/10 dark:hover:bg-primary/20 group transition-all text-left"
                >
                  <span className="text-sm font-medium text-text dark:text-dark-text group-hover:text-primary">
                    {field.label}
                  </span>
                  <Plus
                    size={14}
                    className="text-gray-400 dark:text-dark-muted group-hover:text-primary"
                  />
                </button>
              ))}
            </div>

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
              <div className="flex items-start gap-2">
                <AlertCircle
                  size={16}
                  className="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5"
                />
                <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                  Dynamic fields will be automatically populated with real data
                  when the agreement is generated.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementBuilder;
